import { create } from 'zustand';
import type { AuthState, User } from '../../types';
import { USER_ROLES } from '../../constants';

// Utility to get refresh token from cookies
const getRefreshTokenFromCookie = (): string | null => {
  try {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'refresh_token') {
        return value || null;
      }
    }
    return null;
  } catch {
    return null;
  }
};

interface AuthStore extends AuthState {
  // Actions
  login: (user: User, token: string) => void;
  logout: () => void;
  updateUser: (user: Partial<User>) => void;
  setLoading: (loading: boolean) => void;
  clearError: () => void;
  // Token management
  setTokens: (tokens: { access_token: string; refresh_token: string }) => void;
  updateBalance: (balance: number) => void;
  // Initialize auth from storage
  initializeAuth: () => Promise<boolean>;
  // Computed
  isAdmin: () => boolean;
  isAgent: () => boolean;
  isUser: () => boolean;
  hasRole: (role: string) => boolean;
}

export const useAuthStore = create<AuthStore>((set, get) => ({
  // Initial state
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: false,
  balance: 0,

  // Actions
  login: (user: User, token: string) => {
    if (user && token) {
      const state = {
        user,
        token,
        isAuthenticated: true,
        isLoading: false,
        balance: parseFloat(String(user.balance)) || 0,
      };

      set(state);

      // Save to localStorage (simple format)
      const persistData = {
        access_token: token,
        username: user.username,
        user_type: user.type
      };
      localStorage.setItem('auth-storage', JSON.stringify(persistData));
    } else {
      const state = {
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        balance: 0,
      };

      set(state);
      localStorage.removeItem('auth-storage');
    }
  },

  logout: () => {
    // Clear refresh token cookie
    document.cookie = 'refresh_token=; path=/; max-age=0; SameSite=Lax; Secure';

    const state = {
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      balance: 0,
    };

    set(state);
    localStorage.removeItem('auth-storage');
  },

  updateUser: (userData: Partial<User>) => {
    const currentUser = get().user;
    if (currentUser) {
      const updatedUser = { ...currentUser, ...userData };
      set({
        user: updatedUser,
      });

      // Update localStorage (preserve existing token info)
      const existingData = localStorage.getItem('auth-storage');
      if (existingData) {
        try {
          const parsedData = JSON.parse(existingData);
          const persistData = {
            ...parsedData,
            username: updatedUser.username,
            user_type: updatedUser.type
          };
          localStorage.setItem('auth-storage', JSON.stringify(persistData));
        } catch (error) {
          console.error('Failed to update localStorage:', error);
        }
      }
    }
  },

  setLoading: (loading: boolean) => {
    set({ isLoading: loading });
  },

  clearError: () => {
    // Add error state if needed
  },

  // Token management
  setTokens: (tokens: { access_token: string; refresh_token: string }) => {
    // Set refresh token in secure cookie
    if (tokens.refresh_token) {
      document.cookie = `refresh_token=${tokens.refresh_token}; path=/; max-age=${7 * 24 * 60 * 60}; SameSite=Lax; Secure`;
    }

    set({
      token: tokens.access_token,
    });
  },

  updateBalance: (balance: number) => {
    const currentUser = get().user;
    if (currentUser) {
      const updatedUser = { ...currentUser, balance };
      set({
        user: updatedUser,
        balance: balance,
      });

      // localStorage doesn't need to store balance
      // It will be fetched from server on demand
    }
  },

  // Initialize auth from storage
  initializeAuth: async (): Promise<boolean> => {
    try {
      // Try to load from localStorage
      const storedData = localStorage.getItem('auth-storage');
      if (storedData) {
        try {
          const persistData = JSON.parse(storedData);
          if (persistData.access_token && persistData.username && persistData.user_type) {
            // Create minimal user object with all required properties
            const user: User = {
              id: 0, // Will be fetched from server
              username: persistData.username,
              type: persistData.user_type,
              balance: 0, // Will be fetched from server
              reserved_balance: 0,
              debt_balance: 0,
              credit_limit: 0,
              status: 'active',
              created_at: new Date().toISOString()
            };

            // Restore state
            set({
              user,
              token: persistData.access_token,
              isAuthenticated: true,
              balance: 0,
              isLoading: false,
            });
            return true;
          }
        } catch (parseError) {
          console.error('Failed to parse stored auth data:', parseError);
          localStorage.removeItem('auth-storage');
        }
      }

      return false;
    } catch (error) {
      console.error('Failed to initialize auth:', error);
      get().logout();
      return false;
    }
  },

  // Computed properties
  isAdmin: () => {
    const { user } = get();
    return user?.type === USER_ROLES.ADMIN;
  },

  isAgent: () => {
    const { user } = get();
    return user?.type === USER_ROLES.AGENT;
  },

  isUser: () => {
    const { user } = get();
    return user?.type === USER_ROLES.USER;
  },

  hasRole: (role: string) => {
    const { user } = get();
    return user?.type === role;
  },
}));

// Export refresh token utility for external use
export { getRefreshTokenFromCookie };